// src/components/Property/Unit/UnitForm.jsx

import {
    Avatar,
    Backdrop,
    Box,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Container,
    Divider,
    Grid,
    IconButton,
    Skeleton,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    InputAdornment,
    Alert,
} from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Edit, House } from "@mui/icons-material"
import { Helmet } from "react-helmet"
import { useSnackbar } from "notistack"

import { DatePicker } from "@mui/x-date-pickers"
import moment from "moment"

import Context from "../../../contexts/Context"
import { api } from "../../../services/Main/Api"
import { getCondoId, getCondoType } from "../../../services/Main/AuthStorage"

import Owner from "./components/Owner/Owner"
import SaveCancelButton from "../../../components/Shared/SaveCancelButton"
import SimpleButtons from "../../../components/Shared/SimpleButtons"

// ✅ Helpers FORA do componente
const TwoColGrid = ({ children }) => (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {children}
    </Grid>
)

const FieldText = ({ label, value }) => (
    <Typography variant="body2" sx={{ lineHeight: 1.8 }}>
        <b>{label}:</b> {value || "-"}
    </Typography>
)

// ✅ Estados do Brasil: label = nome, value = UF
const STATE_OPTIONS = [
    { value: "AC", label: "Acre" },
    { value: "AL", label: "Alagoas" },
    { value: "AP", label: "Amapá" },
    { value: "AM", label: "Amazonas" },
    { value: "BA", label: "Bahia" },
    { value: "CE", label: "Ceará" },
    { value: "DF", label: "Distrito Federal" },
    { value: "ES", label: "Espírito Santo" },
    { value: "GO", label: "Goiás" },
    { value: "MA", label: "Maranhão" },
    { value: "MT", label: "Mato Grosso" },
    { value: "MS", label: "Mato Grosso do Sul" },
    { value: "MG", label: "Minas Gerais" },
    { value: "PA", label: "Pará" },
    { value: "PB", label: "Paraíba" },
    { value: "PR", label: "Paraná" },
    { value: "PE", label: "Pernambuco" },
    { value: "PI", label: "Piauí" },
    { value: "RJ", label: "Rio de Janeiro" },
    { value: "RN", label: "Rio Grande do Norte" },
    { value: "RS", label: "Rio Grande do Sul" },
    { value: "RO", label: "Rondônia" },
    { value: "RR", label: "Roraima" },
    { value: "SC", label: "Santa Catarina" },
    { value: "SP", label: "São Paulo" },
    { value: "SE", label: "Sergipe" },
    { value: "TO", label: "Tocantins" },
]

const UnitForm = ({ getContract, setUnitData }) => {
    const { loadingModal, dataProvider, getUnitOptions } = useContext(Context)

    const { enqueueSnackbar } = useSnackbar()
    const params = useParams()
    const location = useLocation()
    const navigate = useNavigate()

    const [reqData, setReqData] = useState(null)
    const [edit, setEdit] = useState(false)
    const [error, setError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    // ✅ ViaCEP (somente quando usuário altera o CEP)
    const [cepLoading, setCepLoading] = useState(false)
    const [cepLookupError, setCepLookupError] = useState(null)
    const [cepDirty, setCepDirty] = useState(false)
    const lastCepFetchedRef = useRef(null)
    const cepAbortRef = useRef(null)

    // ✅ id vindo da rota (edição)
    const routeUnitId = params?.id ? Number(params.id) : null
    // ✅ id local (quando cria via POST e ainda não tem rota com id)
    const [localUnitId, setLocalUnitId] = useState(null)

    // ✅ id final (prioriza rota, depois local, depois reqData.id)
    const unitIdResolved = useMemo(() => {
        return routeUnitId ?? localUnitId ?? (reqData?.id ? Number(reqData.id) : null)
    }, [routeUnitId, localUnitId, reqData?.id])

    // ---------- choices ----------
    const UNIT_TYPE_OPTIONS = useMemo(
        () => [
            { value: "house", label: "Casa" },
            { value: "apartment", label: "Apartamento" },
            { value: "commercial_house", label: "Casa comercial" },
            { value: "commercial_room", label: "Sala comercial" },
            { value: "pavilion", label: "Pavilhão" },
            { value: "batch", label: "Lote" },
            { value: "shed", label: "Galpão" },
            { value: "hall", label: "Salão" },
            { value: "shop", label: "Loja" },
            { value: "kitnet", label: "Kitnet" },
            { value: "two-story-house", label: "Casa Sobrado" },
            { value: "single-house", label: "Casa Térrea" },
            { value: "duplex-house", label: "Casa Duplex" },
            { value: "garage", label: "Garagem" },
            { value: "barrack", label: "Barracão" },
            { value: "Outbuilding", label: "Edícula" },
        ],
        []
    )

    const MARITAL_STATUS_OPTIONS = useMemo(
        () => [
            { value: "single", label: "Solteiro(a)" },
            { value: "married", label: "Casado(a)" },
            { value: "divorced", label: "Divorciado(a)" },
            { value: "widowed", label: "Viúvo(a)" },
            { value: "separated", label: "Separado(a)" },
            { value: "stable_union", label: "União Estável" },
        ],
        []
    )

    const maritalStatusLabel = useCallback(
        (value) => {
            const found = MARITAL_STATUS_OPTIONS.find((x) => x.value === value)
            return found?.label || "-"
        },
        [MARITAL_STATUS_OPTIONS]
    )

    const stateLabel = useCallback((uf) => {
        const found = STATE_OPTIONS.find((x) => x.value === uf)
        return found?.label || uf || "-"
    }, [])

    // ---------- defaults ----------
    const defaults = useMemo(
        () => ({
            condo: +getCondoId(),
            unit_type: "apartment",
            owner: false,
            name: "",
            cpf: "",
            number: "",
            phone: "",
            email: "",
            square_meter: 0,
            percent_meter: 0,
            status: "occupied",

            owner_name: "",
            owner_cpf: "",
            owner_phone: "",
            owner_email: "",

            block: "",
            building_meter: "",
            street: "",
            complement: "",
            address_number: "",
            district: "",
            city: "",
            state: "",
            cep: "",
            cep_checked: false,

            name_secondary: "",
            cpf_secondary: "",
            phone_secondary: "",
            email_secondary: "",

            marital_status: "",
            profession: "",
            rg: "",
            nationality: "Brasileira",
            birth_date: null,
        }),
        []
    )

    // ✅ inicializa no modo criação
    useEffect(() => {
        if (routeUnitId) return
        if (reqData) return
        setReqData(defaults)
        setEdit(true)
    }, [routeUnitId, reqData, defaults])

    const setField = useCallback(
        (name, value) => {
            setReqData((prev) => ({ ...(prev || defaults), [name]: value }))
        },
        [defaults]
    )

    const handleChange = useCallback(
        (e) => {
            const { name, value } = e.target
            setField(name, value)
        },
        [setField]
    )

    // ✅ handler específico do CEP
    const handleCepChange = useCallback(
        (e) => {
            const { value } = e.target
            setCepDirty(true)
            setField("cep", value)
        },
        [setField]
    )

    // ---------- API ----------
    const getUnit = useCallback(
        async (load = true, explicitId) => {
            if (!getCondoId()) return
            if (load) setIsLoading(true)

            const idToFetch = explicitId ?? unitIdResolved
            if (!idToFetch) {
                if (load) setIsLoading(false)
                return
            }

            try {
                const response = await api.get(`unit/${idToFetch}`)
                const hydrated = { ...defaults, ...response.data, condo: +getCondoId() }

                setReqData(hydrated)
                setLocalUnitId(response?.data?.id ?? idToFetch)

                // ✅ carregou via API -> NÃO dispara ViaCEP
                setCepDirty(false)
                const cepDigits = String(hydrated?.cep ?? "").replace(/[^\d]+/g, "")
                lastCepFetchedRef.current = cepDigits.length === 8 ? cepDigits : null
                setCepLookupError(null)
            } catch (err) {
                console.log(err)
            } finally {
                if (load) setIsLoading(false)
            }
        },
        [defaults, unitIdResolved]
    )

    // ✅ Sempre que mudar a rota (/imoveis/:id), busca o imóvel correto
    useEffect(() => {
        if (!routeUnitId) return
        setReqData(null)
        setError(null)
        setEdit(false)
        setLocalUnitId(null)
        setCepDirty(false)
        getUnit(true, routeUnitId)
    }, [routeUnitId, getUnit])

    const normalizeDigits = (v) => String(v ?? "").replace(/[^\d]+/g, "")
    const normalizeDecimal = (v) => String(v ?? "").replace(/,/g, ".")

    const buildPayload = useCallback(() => {
        const data = {
            ...defaults,
            ...reqData,
            condo: +getCondoId(),
            owner: true, // ajuste se isso depende de renter (no teu original era !filters.renter)

            phone: normalizeDigits(reqData?.phone),
            cpf_visitor: normalizeDigits(reqData?.cpf),
            percent_meter: normalizeDecimal(reqData?.percent_meter ? reqData?.percent_meter : 0),

            marital_status: reqData?.marital_status || "",
            profession: (reqData?.profession || "").trim(),
            rg: (reqData?.rg || "").trim(),
            nationality: (reqData?.nationality || "Brasileira").trim(),
            birth_date: reqData?.birth_date ? moment(reqData.birth_date).format("YYYY-MM-DD") : null,
        }
        return data
    }, [defaults, reqData])

    // ✅ save único: cria (POST) se não tiver id, senão atualiza (PUT)
    const save = useCallback(async () => {
        setIsLoading(true)
        setError(null)

        const rentPath = location?.pathname?.includes("contratos")
        const payload = buildPayload()

        try {
            if (!unitIdResolved) {
                // ✅ CREATE
                const resp = await api.post("unit/", payload)
                const createdId = resp?.data?.id

                if (!createdId) {
                    enqueueSnackbar("Imóvel criado, mas a API não retornou o ID", { variant: "warning" })
                    setReqData((prev) => ({ ...(prev || defaults), ...(resp?.data || {}) }))
                    return
                }

                setLocalUnitId(createdId)
                setReqData((prev) => ({ ...(prev || defaults), ...(resp?.data || {}), id: createdId }))

                enqueueSnackbar("Imóvel criado", { variant: "success" })

                // ✅ opcional (recomendado): vai para a rota de edição
                navigate(`/alugueis/imoveis/${createdId}/editar`, { replace: true, state: { edit: true } })
                setEdit(true)
            } else {
                // ✅ UPDATE
                const resp = await api.put(`unit/${unitIdResolved}/`, payload)
                const updatedId = resp?.data?.id ?? unitIdResolved

                setLocalUnitId(updatedId)
                await getUnit(false, updatedId)

                rentPath && getContract?.(null, true)
                rentPath && getUnitOptions?.()

                enqueueSnackbar("Imóvel atualizado", { variant: "success" })
                setEdit(false)
            }
        } catch (err) {
            const respData = err?.response?.data
            enqueueSnackbar("Erro ao salvar o imóvel", { variant: "error" })
            setError(respData || { detail: "Erro ao salvar" })
            console.log(err)
        } finally {
            setIsLoading(false)
        }
    }, [buildPayload, defaults, enqueueSnackbar, getContract, getUnit, getUnitOptions, location?.pathname, navigate, unitIdResolved])

    // ---------- ViaCEP ----------
    const fetchAddressByCep = useCallback(
        async (cepDigits) => {
            if (lastCepFetchedRef.current === cepDigits) return

            try {
                cepAbortRef.current?.abort?.()
            } catch (e) {}

            const controller = new AbortController()
            cepAbortRef.current = controller

            setCepLoading(true)
            setCepLookupError(null)

            try {
                const res = await fetch(`https://viacep.com.br/ws/${cepDigits}/json/`, { signal: controller.signal })
                if (!res.ok) throw new Error("Falha ao consultar CEP")

                const data = await res.json()

                if (data?.erro) {
                    lastCepFetchedRef.current = null
                    setCepLookupError("CEP não encontrado")
                    setReqData((prev) => ({ ...(prev || defaults), cep_checked: false }))
                    return
                }

                lastCepFetchedRef.current = cepDigits

                setReqData((prev) => {
                    const p = prev || defaults
                    return {
                        ...p,
                        street: data?.logradouro ? data.logradouro : p.street,
                        complement: data?.complemento ? data.complemento : p.complement,
                        district: data?.bairro ? data.bairro : p.district,
                        city: data?.localidade ? data.localidade : p.city,
                        state: data?.uf ? data.uf : p.state,
                        cep_checked: true,
                    }
                })
            } catch (err) {
                if (err?.name === "AbortError") return
                lastCepFetchedRef.current = null
                setCepLookupError("Não foi possível consultar o CEP")
                setReqData((prev) => ({ ...(prev || defaults), cep_checked: false }))
            } finally {
                setCepLoading(false)
            }
        },
        [defaults]
    )

    useEffect(() => {
        if (!cepDirty) return

        const cep = reqData?.cep
        if (!cep) {
            setCepLookupError(null)
            setCepLoading(false)
            lastCepFetchedRef.current = null
            setCepDirty(false)
            return
        }

        const cepDigits = String(cep).replace(/[^\d]+/g, "")

        if (cepDigits.length !== 8) {
            setCepLookupError(null)
            setReqData((prev) => ({ ...(prev || defaults), cep_checked: false }))
            lastCepFetchedRef.current = null
            return
        }

        const t = setTimeout(() => {
            Promise.resolve(fetchAddressByCep(cepDigits)).finally(() => setCepDirty(false))
        }, 450)

        return () => clearTimeout(t)
    }, [cepDirty, reqData?.cep, fetchAddressByCep, defaults])

    // ---------- render blocks ----------
    const renderClientFieldsEdit = () => (
        <TwoColGrid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Nome do Inquilino"
                    name="name"
                    value={reqData?.name ?? ""}
                    onChange={handleChange}
                    error={!!error?.name}
                    helperText={error?.name?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="CPF ou CNPJ"
                    name="cpf"
                    value={reqData?.cpf ?? ""}
                    onChange={handleChange}
                    error={!!error?.cpf}
                    helperText={error?.cpf?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Telefone"
                    name="phone"
                    value={reqData?.phone ?? ""}
                    onChange={handleChange}
                    error={!!error?.phone}
                    helperText={error?.phone?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={reqData?.email ?? ""}
                    onChange={handleChange}
                    error={!!error?.email}
                    helperText={error?.email?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!error?.marital_status}>
                    <InputLabel id="marital_status-label">Estado civil</InputLabel>
                    <Select
                        labelId="marital_status-label"
                        label="Estado civil"
                        name="marital_status"
                        value={reqData?.marital_status ?? ""}
                        onChange={handleChange}
                    >
                        <MenuItem value="">
                            <em>Não informado</em>
                        </MenuItem>
                        {MARITAL_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Profissão"
                    name="profession"
                    value={reqData?.profession ?? ""}
                    onChange={handleChange}
                    error={!!error?.profession}
                    helperText={error?.profession?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="RG"
                    name="rg"
                    value={reqData?.rg ?? ""}
                    onChange={handleChange}
                    error={!!error?.rg}
                    helperText={error?.rg?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Nacionalidade"
                    name="nationality"
                    value={reqData?.nationality ?? "Brasileira"}
                    onChange={handleChange}
                    error={!!error?.nationality}
                    helperText={error?.nationality?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <DatePicker
                    label="Data de Nascimento"
                    value={reqData?.birth_date ? new Date(reqData.birth_date + "T00:00:00") : null}
                    onChange={(newValue) =>
                        setField("birth_date", newValue ? moment(newValue).format("YYYY-MM-DD") : null)
                    }
                    format="dd/MM/yyyy"
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            error: !!error?.birth_date,
                            helperText: error?.birth_date?.[0],
                        },
                    }}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Nome do Inquilino (secundário)"
                    name="name_secondary"
                    value={reqData?.name_secondary ?? ""}
                    onChange={handleChange}
                    error={!!error?.name_secondary}
                    helperText={error?.name_secondary?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="CPF/CNPJ (secundário)"
                    name="cpf_secondary"
                    value={reqData?.cpf_secondary ?? ""}
                    onChange={handleChange}
                    error={!!error?.cpf_secondary}
                    helperText={error?.cpf_secondary?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Telefone (secundário)"
                    name="phone_secondary"
                    value={reqData?.phone_secondary ?? ""}
                    onChange={handleChange}
                    error={!!error?.phone_secondary}
                    helperText={error?.phone_secondary?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Email (secundário)"
                    name="email_secondary"
                    value={reqData?.email_secondary ?? ""}
                    onChange={handleChange}
                    error={!!error?.email_secondary}
                    helperText={error?.email_secondary?.[0]}
                />
            </Grid>
        </TwoColGrid>
    )

    const renderClientFieldsRead = () => (
        <TwoColGrid>
            <Grid item xs={12} md={6}>
                <FieldText label="Nome" value={reqData?.name} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="CPF/CNPJ" value={reqData?.cpf} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Telefone" value={reqData?.phone} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Email" value={reqData?.email} />
            </Grid>

            <Grid item xs={12} md={6}>
                <FieldText label="Estado civil" value={maritalStatusLabel(reqData?.marital_status)} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Profissão" value={reqData?.profession} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="RG" value={reqData?.rg} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Nacionalidade" value={reqData?.nationality} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText
                    label="Data de Nascimento"
                    value={reqData?.birth_date ? moment(reqData.birth_date).format("DD/MM/YYYY") : null}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <FieldText label="Nome (sec.)" value={reqData?.name_secondary} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="CPF/CNPJ (sec.)" value={reqData?.cpf_secondary} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Telefone (sec.)" value={reqData?.phone_secondary} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Email (sec.)" value={reqData?.email_secondary} />
            </Grid>
        </TwoColGrid>
    )

    const renderUnitFieldsEdit = () => (
        <TwoColGrid>
            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="CEP"
                    name="cep"
                    value={reqData?.cep ?? ""}
                    onChange={handleCepChange}
                    error={!!error?.cep || !!cepLookupError}
                    helperText={error?.cep?.[0] || cepLookupError || (reqData?.cep_checked ? "Endereço preenchido automaticamente" : "")}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">{cepLoading ? <CircularProgress size={18} /> : null}</InputAdornment>
                        ),
                    }}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Rua"
                    name="street"
                    value={reqData?.street ?? ""}
                    onChange={handleChange}
                    error={!!error?.street}
                    helperText={error?.street?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Número"
                    name="number"
                    value={reqData?.number ?? ""}
                    onChange={handleChange}
                    error={!!error?.number}
                    helperText={error?.number?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Complemento"
                    name="complement"
                    value={reqData?.complement ?? ""}
                    onChange={handleChange}
                    error={!!error?.complement}
                    helperText={error?.complement?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Bairro"
                    name="district"
                    value={reqData?.district ?? ""}
                    onChange={handleChange}
                    error={!!error?.district}
                    helperText={error?.district?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextField
                    fullWidth
                    label="Cidade"
                    name="city"
                    value={reqData?.city ?? ""}
                    onChange={handleChange}
                    error={!!error?.city}
                    helperText={error?.city?.[0]}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!error?.state}>
                    <InputLabel id="state-label">Estado</InputLabel>
                    <Select labelId="state-label" label="Estado" name="state" value={reqData?.state ?? ""} onChange={handleChange}>
                        <MenuItem value="">
                            <em>Não informado</em>
                        </MenuItem>
                        {STATE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!error?.unit_type}>
                    <InputLabel id="unit_type-label">Tipo de unidade</InputLabel>
                    <Select
                        labelId="unit_type-label"
                        label="Tipo de unidade"
                        name="unit_type"
                        value={reqData?.unit_type ?? "apartment"}
                        onChange={handleChange}
                    >
                        {UNIT_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>

            {getCondoType() === "CONDO" && (
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="% do condomínio"
                        name="percent_meter"
                        value={reqData?.percent_meter ?? ""}
                        onChange={handleChange}
                        error={!!error?.percent_meter}
                        helperText={error?.percent_meter?.[0]}
                    />
                </Grid>
            )}
        </TwoColGrid>
    )

    const renderUnitFieldsRead = () => (
        <TwoColGrid>
            <Grid item xs={12} md={6}>
                <FieldText label="Tipo" value={UNIT_TYPE_OPTIONS.find((x) => x.value === reqData?.unit_type)?.label} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Número" value={reqData?.number} />
            </Grid>

            {getCondoType() === "CONDO" && (
                <Grid item xs={12} md={6}>
                    <FieldText label="% do condomínio" value={reqData?.percent_meter} />
                </Grid>
            )}

            <Grid item xs={12} md={6}>
                <FieldText label="Rua" value={reqData?.street} />
            </Grid>

            <Grid item xs={12} md={6}>
                <FieldText label="Complemento" value={reqData?.complement} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Bairro" value={reqData?.district} />
            </Grid>
            <Grid item xs={12} md={6}>
                <FieldText label="Cidade" value={reqData?.city} />
            </Grid>

            <Grid item xs={12} md={6}>
                <FieldText label="Estado" value={stateLabel(reqData?.state)} />
            </Grid>

            <Grid item xs={12} md={6}>
                <FieldText label="CEP" value={reqData?.cep} />
            </Grid>
        </TwoColGrid>
    )

    // ---------- render ----------
    return (
        <>
            <Helmet>
                <title>{!params.id ? "Criar imóvel - Bloco B3" : "Editar imóvel - Bloco B3"}</title>
            </Helmet>

            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingModal}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Container maxWidth={false} disableGutters={!!dataProvider?.modalUnit}>
                <Grid container spacing={2} sx={{ display: "flex", p: 0 }}>
                    <Grid item xs={12}>
                        {reqData ? (
                            <Card>
                                <CardHeader
                                    avatar={
                                        <Avatar sx={{ bgcolor: "primary.main" }}>
                                            <House />
                                        </Avatar>
                                    }
                                    title={"Imóvel"}
                                    action={
                                        unitIdResolved ? (
                                            <IconButton onClick={() => setEdit(true)}>
                                                <Edit />
                                            </IconButton>
                                        ) : null
                                    }
                                />
                                <Divider sx={{ mb: ".3rem" }} />

                                <CardContent>
                                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                        <Typography
                                            sx={{
                                                fontWeight: "bold",
                                                fontSize: "18px",
                                                color: "secondary.main",
                                                mb: "2rem",
                                            }}
                                        >
                                            Informações
                                        </Typography>
                                    </Box>

                                    {(edit || !unitIdResolved) ? (
                                        <>
                                            {renderUnitFieldsEdit()}
                                            <Divider sx={{ my: 2 }} />
                                            {renderClientFieldsEdit()}

                                            <Divider sx={{ mt: "1rem" }} />
                                            <SaveCancelButton
                                                edit={true}
                                                removeDelete
                                                saveClick={save}
                                                cancelClick={() => {
                                                    if (unitIdResolved) {
                                                        getUnit(false, unitIdResolved)
                                                        setEdit(false)
                                                        setError(null)
                                                    } else {
                                                        // ainda não criou: só reseta pro defaults
                                                        setReqData(defaults)
                                                        setError(null)
                                                    }
                                                }}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            {renderUnitFieldsRead()}
                                            <Divider sx={{ my: 2 }} />
                                            {renderClientFieldsRead()}
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <Skeleton variant="rounded" height={743.594} animation="pulse" />
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                {unitIdResolved && (
                                    <Owner unitId={unitIdResolved} />
                                )}
                            </Grid>

                            <Grid item xs={12}>{/* <GarageFormSet /> */}</Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {!dataProvider?.modalUnit && <SimpleButtons backButton={"Voltar"} />}
            </Container>
        </>
    )
}

export default UnitForm
